// ============================================================================
// StudentListBase.js
//
// Shared internals for the View / Update / Delete Students pages — filters,
// table, pagination, promotion, and withdrawal logic all live here so every
// thin page component stays in sync.
//
// mode="manage"  → all four action buttons (View · Edit · Promote · Delete)
//                  are rendered inline on this single page.
//                  • View    → full-page detail view (read-only)
//                  • Edit    → slide-in panel that embeds <StudentForm>
//                  • Promote → modal (single or bulk)
//                  • Delete  → inline confirmation modal
//                  • Withdraw→ modal with date + reason
//
// A dedicated "Withdrawn Students" full-page view is also available from the
// header, listing every withdrawn student with search/filter/export/reactivate.
// Withdrawn students are excluded from the main list below since they already
// have their own dedicated page.
//
// All buttons are individually gated by canEdit / canDelete / canWithdraw so
// each role only sees the actions it is allowed to perform.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import { confirmDialog } from "../../../utils/dialogs";
import StudentForm from "./StudentForm";
import {
  ArrowLeftIcon,
  SearchIcon,
  AlertIcon,
  EditIcon,
  DeleteIcon,
  PromoteIcon,
  ViewIcon,
} from "../icons/Icons";
import { getStoredCampusSections } from "../../../utils/campusSections";
import {
  classSessionIdsOf,
  filterClassesBySession,
  sectionsForClassSession,
  uniqueClassNames,
} from "../../../utils/campusClasses";

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens — matches the ALS Portal palette
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  navy:       "#0f1b2d",
  blue:       "#1d4ed8",
  blueSoft:   "#e8f0fe",
  red:        "#dc2626",
  redSoft:    "#fff5f5",
  green:      "#16a34a",
  greenSoft:  "#f0fff4",
  amber:      "#d97706",
  amberSoft:  "#fffbeb",
  slate:      "#64748b",
  slateLight: "#94a3b8",
  border:     "#e2e8f0",
  bg:         "#f4f6f9",
  white:      "#ffffff",
  text:       "#1a202c",
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared style helpers
// ─────────────────────────────────────────────────────────────────────────────
const inputSt = {
  padding: "9px 13px",
  borderRadius: "8px",
  border: `1.5px solid ${C.border}`,
  fontSize: "13px",
  color: C.text,
  background: C.white,
  outline: "none",
  boxSizing: "border-box",
};

const labelSt = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: C.slate,
  marginBottom: "5px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};

const errorSt = {
  display: "block",
  fontSize: "11px",
  color: C.red,
  marginTop: "4px",
  fontWeight: 600,
};

// ─────────────────────────────────────────────────────────────────────────────
// Small reusable components
// ─────────────────────────────────────────────────────────────────────────────
const Btn = ({ children, onClick, disabled, variant = "primary", style: extra = {} }) => {
  const variants = {
    primary: { background: C.navy,  color: "#fff",  border: "none" },
    ghost:   { background: C.white, color: C.slate, border: `1.5px solid ${C.border}` },
    danger:  { background: C.red,   color: "#fff",  border: "none" },
    amber:   { background: C.amber, color: "#fff",  border: "none" },
    green:   { background: C.green, color: "#fff",  border: "none" },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "9px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      display: "inline-flex", alignItems: "center", gap: "7px",
      whiteSpace: "nowrap", transition: "opacity .15s",
      ...v, ...extra,
    }}>
      {children}
    </button>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    Active:    [C.greenSoft, C.green],
    Withdrawn: [C.redSoft,   C.red],
    Inactive:  ["#f1f5f9",   C.slate],
    Graduated: [C.blueSoft,  C.blue],
  };
  const [bg, color] = map[status] || ["#f1f5f9", C.slate];
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: bg, color }}>
      {status || "—"}
    </span>
  );
};

const TH = ({ children, center, right }) => (
  <th style={{ textAlign: right ? "right" : center ? "center" : "left", padding: "10px 16px", fontSize: "11px", fontWeight: 700, color: C.slate, background: C.bg, borderBottom: `2px solid ${C.border}`, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>
    {children}
  </th>
);

const TD = ({ children, center, mono, right }) => (
  <td style={{ padding: "12px 16px", fontSize: "13px", color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle", textAlign: right ? "right" : center ? "center" : "left", fontFamily: mono ? "monospace" : undefined }}>
    {children}
  </td>
);

/** Labelled read-only row used inside the View detail page. */
const DetailRow = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
    <span style={{ fontSize: "10px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</span>
    <span style={{ fontSize: "13px", color: C.text, fontWeight: 500 }}>{value || "—"}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Spinner helper
// ─────────────────────────────────────────────────────────────────────────────
const Spinner = ({ color = C.navy, size = 13 }) => (
  <span style={{
    width: size, height: size,
    border: `2px solid #fff`,
    borderTopColor: "transparent",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin .7s linear infinite",
  }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// Mode config — now hardcoded to "manage" only (other modes removed)
// ─────────────────────────────────────────────────────────────────────────────
const MODE = {
  manage: {

    accentColor: C.navy,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Promotion — order campus classes and build next class+section options
// ─────────────────────────────────────────────────────────────────────────────
const CLASS_LADDER = ["Nursery", "PG", "KG", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

const getClassOrderIndex = (className) =>
  CLASS_LADDER.findIndex((c) => c.toLowerCase() === String(className || "").trim().toLowerCase());

/** Lower grades first (Nursery…X / 1…10), then section, then earliest id first. */
const classSortRank = (className) => {
  const name = String(className || "").trim();
  const ladderIdx = getClassOrderIndex(name);
  if (ladderIdx !== -1) return ladderIdx;
  const early = { nursery: 0, pg: 1, playgroup: 1, kg: 2, kindergarten: 2 };
  const key = name.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(early, key)) return early[key];
  const digit = name.match(/\d+/);
  if (digit) return 2 + Number(digit[0]); // Class 1 → same band as I
  return 999;
};

const sortStudentsAscending = (list = []) =>
  [...list].sort((a, b) => {
    const rankDiff = classSortRank(a.class_name || a.class) - classSortRank(b.class_name || b.class);
    if (rankDiff !== 0) return rankDiff;
    const secDiff = String(a.class_section || a.section || "").localeCompare(
      String(b.class_section || b.section || ""),
      undefined,
      { numeric: true, sensitivity: "base" },
    );
    if (secDiff !== 0) return secDiff;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });

const encodePromoteTarget = (className, section = "") =>
  JSON.stringify({ className, section: String(section || "").trim() });

const decodePromoteTarget = (value) => {
  if (!value || value === "Graduate") return { className: "Graduate", section: "" };
  try {
    const parsed = JSON.parse(value);
    return {
      className: parsed.className || "",
      section: String(parsed.section || "").trim(),
    };
  } catch {
    return { className: value, section: "" };
  }
};

const formatPromoteLabel = (className, section) =>
  section ? `${className} - ${section}` : className;

/** Expand campus class rows into selectable class+section promote targets */
const expandClassSectionOptions = (campusClasses = [], storedSections = []) => {
  const byName = new Map();
  campusClasses.forEach((cls) => {
    const name = String(cls?.name || "").trim();
    if (!name) return;
    if (!byName.has(name)) byName.set(name, []);
    byName.get(name).push(cls);
  });

  const options = [];
  byName.forEach((rows, name) => {
    const withSection = rows.filter((r) => String(r.section || "").trim());
    if (withSection.length) {
      const seen = new Set();
      withSection.forEach((r) => {
        const section = String(r.section).trim();
        const key = section.toUpperCase();
        if (seen.has(key)) return;
        seen.add(key);
        options.push({
          className: name,
          section,
          label: formatPromoteLabel(name, section),
          value: encodePromoteTarget(name, section),
        });
      });
    } else if (storedSections.length) {
      storedSections.forEach((section) => {
        options.push({
          className: name,
          section,
          label: formatPromoteLabel(name, section),
          value: encodePromoteTarget(name, section),
        });
      });
    } else {
      options.push({
        className: name,
        section: "",
        label: name,
        value: encodePromoteTarget(name, ""),
      });
    }
  });
  return options;
};

/** Keep only classes after the student's current class (campus ladder order) */
const getNextPromoteOptions = (allOptions = [], currentClassName = "") => {
  const names = [];
  allOptions.forEach((opt) => {
    if (!names.some((n) => n.toLowerCase() === opt.className.toLowerCase())) {
      names.push(opt.className);
    }
  });

  names.sort((a, b) => {
    const ai = getClassOrderIndex(a);
    const bi = getClassOrderIndex(b);
    const ao = ai === -1 ? 999 : ai;
    const bo = bi === -1 ? 999 : bi;
    if (ao !== bo) return ao - bo;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
  });

  const current = String(currentClassName || "").trim().toLowerCase();
  const currentIdx = names.findIndex((n) => n.toLowerCase() === current);
  const nextNames = new Set(
    (currentIdx === -1
      ? names.filter((n) => n.toLowerCase() !== current)
      : names.slice(currentIdx + 1)
    ).map((n) => n.toLowerCase()),
  );

  return allOptions
    .filter((opt) => nextNames.has(opt.className.toLowerCase()))
    .sort((a, b) => {
      const ai = getClassOrderIndex(a.className);
      const bi = getClassOrderIndex(b.className);
      const ao = ai === -1 ? 999 : ai;
      const bo = bi === -1 ? 999 : bi;
      if (ao !== bo) return ao - bo;
      return String(a.section).localeCompare(String(b.section), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// Slide-in overlay wrapper
// Used for the Edit panel.
// ─────────────────────────────────────────────────────────────────────────────
const SlidePanel = ({ open, onClose, title, subtitle, accentColor = C.navy, children, wide = false }) => {
  // Prevent body scroll while panel is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1100,
      display: "flex", justifyContent: "flex-end",
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(15,27,45,0.55)", backdropFilter: "blur(2px)", cursor: "pointer" }}
      />

      {/* Panel */}
      <div style={{
        position: "relative", zIndex: 1,
        width: wide ? "min(95vw, 860px)" : "min(95vw, 520px)",
        height: "100%",
        background: C.white,
        boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        animation: "slideIn .22s ease",
      }}>
        {/* Panel header */}
        <div style={{ background: accentColor, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>{title}</div>
            {subtitle && <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.65)", marginTop: "3px" }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: "7px", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", lineHeight: 1, flexShrink: 0 }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px" }}>
          {children}
        </div>
      </div>

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FULL-PAGE STUDENT VIEW — replaces the whole screen (not a side dialog)
// ─────────────────────────────────────────────────────────────────────────────
const FullPageStudentView = ({ student, onBack, onEditClick, canEdit }) => {
  if (!student) return null;
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 38, height: 38, borderRadius: "9px",
          border: `1.5px solid ${C.border}`, background: C.white,
          cursor: "pointer", color: C.slate, flexShrink: 0,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
          <ArrowLeftIcon />
        </button>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 800, color: C.text, margin: 0 }}>Student Details</h1>
          
        </div>
        {canEdit && (
          <div style={{ marginLeft: "auto" }}>
          
          </div>
        )}
      </div>

      {/* Detail card */}
      <div style={{ background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", maxWidth: "920px" }}>

        {/* Profile strip */}
        <div style={{ background: C.navy, padding: "26px 28px", display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(255,255,255,0.15)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "26px", flexShrink: 0 }}>
            {(student.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: "20px", color: "#fff" }}>{student.name}</div>
            <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.65)", fontFamily: "monospace", marginTop: "3px" }}>{student.student_id || student.id}</div>
            <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <StatusBadge status={student.student_status} />
            </div>
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: "24px 28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0 40px" }}>
            <DetailRow label="Gender"          value={student.gender} />
            <DetailRow label="Date of Birth"   value={student.date_of_birth} />
            <DetailRow label="Class"           value={student.class_name ? `${student.class_name}${student.class_section ? ` — Section ${student.class_section}` : ""}` : undefined} />
            <DetailRow label="Admission Date"  value={student.admission_date} />
            <DetailRow label="Academic Session" value={student.academic_session} />
            <DetailRow label="Fee Amount"      value={student.actual_fee_amount != null ? `PKR ${Number(student.actual_fee_amount).toLocaleString()}` : undefined} />
            <DetailRow label="Fee Plan"        value={student.fee_plan_name} />
            <DetailRow label="Campus"          value={student.campus_name} />
            {student.student_status === "Withdrawn" && (
              <>
                <DetailRow label="Withdrawal Date"   value={student.withdrawal_date} />
                <DetailRow label="Withdrawal Reason" value={student.withdrawal_reason} />
              </>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ padding: "16px 28px 24px", display: "flex", gap: "10px", borderTop: `1px solid ${C.border}` }}>
          
          {canEdit && (
            <Btn onClick={() => { onEditClick(student); }}>
              <EditIcon /> Edit Record
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WITHDRAWN STUDENTS PAGE — full page listing every withdrawn student
// ─────────────────────────────────────────────────────────────────────────────
const WithdrawnStudentsPage = ({ onBack, onLogout, showToast }) => {
  const [students,     setStudents]     = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [error,        setError]        = useState("");
  const [searchTerm,   setSearchTerm]   = useState("");
  const [classFilter,  setClassFilter]  = useState("");
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(25);
  const [viewStudent,  setViewStudent]  = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [isReactivating,   setIsReactivating]   = useState(false);

  const fetchWithdrawn = useCallback(async () => {
    const token  = localStorage.getItem("als_token");
    const params = new URLSearchParams();
    params.set("student_status", "Withdrawn");
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (classFilter)       params.set("class_name", classFilter);

    setIsLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/students?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout?.(); return; }
      if (!res.ok) throw new Error("Unable to load withdrawn students");
      const data = await res.json();
      setStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load withdrawn students");
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, classFilter, onLogout]);

  useEffect(() => {
    const delay = searchTerm ? 350 : 0;
    const timer = setTimeout(() => { fetchWithdrawn(); }, delay);
    return () => clearTimeout(timer);
  }, [fetchWithdrawn]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, classFilter]);

  const classes = useMemo(
    () => [...new Set(students.map((s) => s.class_name).filter(Boolean))].sort(),
    [students],
  );

  const totalPages = Math.max(1, Math.ceil(students.length / pageSize));
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

  const paged = useMemo(
    () => students.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [students, currentPage, pageSize],
  );

  const clearFilters = () => { setSearchTerm(""); setClassFilter(""); };

  const handleExportCSV = () => {
    const rows = [
      ["#", "Name", "Student ID", "Class", "Withdrawal Date", "Reason", "Admission Date"],
      ...students.map((s, i) => [
        i + 1, s.name, s.student_id || s.id, s.class_name || "—",
        s.withdrawal_date || "—", s.withdrawal_reason || "—", s.admission_date || "—",
      ]),
    ];
    const csv  = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url  = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url; link.download = `Withdrawn_Students_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  };

  const handleReactivateConfirm = async () => {
    if (!reactivateTarget) return;
    const token   = localStorage.getItem("als_token");
    const apiId   = reactivateTarget.student_id || reactivateTarget.id;
    setIsReactivating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/students/${apiId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ student_status: "Active", withdrawal_date: null, withdrawal_reason: null }),
      });
      if (res.status === 401) { onLogout?.(); return; }
      if (!res.ok) throw new Error("Failed to reactivate student");
      setStudents((prev) => prev.filter((s) => s.id !== reactivateTarget.id));
      setReactivateTarget(null);
      showToast?.("success", "Student Reactivated", `${reactivateTarget.name} is now Active.`);
    } catch (err) {
      console.error(err);
      showToast?.("error", "Reactivation Failed", "Could not reactivate the student.");
    } finally {
      setIsReactivating(false);
    }
  };

  // Full-page detail view of a withdrawn student (nested full-page view)
  if (viewStudent) {
    return (
      <FullPageStudentView
        student={viewStudent}
        onBack={() => setViewStudent(null)}
        onEditClick={() => {}}
        canEdit={false}
      />
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 38, height: 38, borderRadius: "9px",
          border: `1.5px solid ${C.border}`, background: C.white,
          cursor: "pointer", color: C.slate, flexShrink: 0,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
          <ArrowLeftIcon />
        </button>
        
        <div style={{ marginLeft: "auto" }}>
          <Btn variant="ghost" onClick={handleExportCSV} disabled={students.length === 0}>
            ⬇ Export CSV
          </Btn>
        </div>
      </div>

      {/* Card */}
      <div style={{ background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>

        {/* Card header */}
        <div style={{ background: C.red, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>Withdrawn Records</span>
          {!isLoading && (
            <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.85)", background: "rgba(255,255,255,0.15)", padding: "3px 12px", borderRadius: "20px", fontWeight: 600 }}>
              {students.length} student{students.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Filters */}
        <div style={{ padding: "14px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", flex: "1 1 220px" }}>
            <label style={labelSt}>Search</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: C.slateLight, display: "flex", pointerEvents: "none" }}>
                <SearchIcon />
              </span>
              <input
                type="text"
                placeholder="Name or student ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...inputSt, width: "100%", paddingLeft: "36px" }}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: "130px" }}>
            <label style={labelSt}>Class</label>
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={inputSt}>
              <option value="">All Classes</option>
              {classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {(searchTerm || classFilter) && (
            <Btn variant="ghost" onClick={clearFilters}>Clear Filters</Btn>
          )}
        </div>

        {/* Table / states */}
        {isLoading ? (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ width: 40, height: 40, border: `3px solid ${C.red}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 16px" }} />
            <div style={{ fontWeight: 700, fontSize: "14px", color: C.text }}>Loading withdrawn students…</div>
          </div>
        ) : error ? (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ fontSize: "36px", marginBottom: "12px" }}>⚠️</div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.red, marginBottom: "6px" }}>Could Not Load Records</div>
            <div style={{ fontSize: "13px", color: C.slate, marginBottom: "16px" }}>{error}</div>
            <Btn onClick={fetchWithdrawn}>Retry</Btn>
          </div>
        ) : students.length === 0 ? (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>📭</div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No Withdrawn Students</div>
            <div style={{ fontSize: "13px" }}>No students have been withdrawn yet.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <TH>#</TH>
                  <TH>Student ID</TH>
                  <TH>Name</TH>
                  <TH>Class</TH>
                  <TH>Withdrawal Date</TH>
                  <TH>Reason</TH>
                  <TH center>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {paged.map((student, idx) => {
                  const rowBg = idx % 2 === 0 ? C.white : "#fafbfd";
                  return (
                    <tr key={student.id} style={{ background: rowBg }}
                      onMouseEnter={(e) => e.currentTarget.style.background = C.redSoft}
                      onMouseLeave={(e) => e.currentTarget.style.background = rowBg}>
                      <TD><span style={{ color: C.slateLight, fontWeight: 600, fontSize: "12px" }}>{(currentPage - 1) * pageSize + idx + 1}</span></TD>
                      <TD mono><strong>{student.student_id || student.id}</strong></TD>
                      <TD>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.slate, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "13px", flexShrink: 0 }}>
                            {(student.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 700 }}>{student.name}</span>
                        </div>
                      </TD>
                      <TD>{student.class_name || "—"}</TD>
                      <TD>{student.withdrawal_date || "—"}</TD>
                      <TD><span style={{ fontSize: "12.5px" }}>{student.withdrawal_reason || "—"}</span></TD>
                      <TD center>
                        <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                          <button
                            onClick={() => setViewStudent(student)}
                            title="View Full Details"
                            style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.blue, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <ViewIcon />
                          </button>
                          <button
                            onClick={() => setReactivateTarget(student)}
                            title="Reactivate Student"
                            style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.green, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <PromoteIcon />
                          </button>
                        </div>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && students.length > 0 && (
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", background: C.bg }}>
            <div style={{ fontSize: "12px", color: C.slate }}>
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong>–<strong>{Math.min(currentPage * pageSize, students.length)}</strong> of <strong>{students.length}</strong>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} style={{ ...inputSt, padding: "6px 10px", width: "auto" }}>
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <Btn variant="ghost" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "6px 14px" }}>
                ‹ Prev
              </Btn>
              <span style={{ fontSize: "12px", color: C.slate, fontWeight: 600, minWidth: "90px", textAlign: "center" }}>
                Page {currentPage} of {totalPages}
              </span>
              <Btn variant="ghost" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "6px 14px" }}>
                Next ›
              </Btn>
            </div>
          </div>
        )}
      </div>

      {/* Reactivate confirmation modal */}
      {reactivateTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, backdropFilter: "blur(2px)", padding: "16px" }}>
          <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 440px)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
            <div style={{ background: C.green, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Reactivate Student</div>
              <button onClick={() => !isReactivating && setReactivateTarget(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: "pointer", fontSize: "16px" }}>✕</button>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ background: C.bg, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "16px", flexShrink: 0 }}>
                  {(reactivateTarget.name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "14px", color: C.text }}>{reactivateTarget.name}</div>
                  <div style={{ fontSize: "12px", color: C.slate, marginTop: "1px" }}>{reactivateTarget.student_id || reactivateTarget.id}</div>
                </div>
              </div>
              <div style={{ background: C.greenSoft, border: `1px solid ${C.green}33`, borderRadius: "8px", padding: "10px 14px", fontSize: "12.5px", color: C.green, fontWeight: 600, marginBottom: "20px" }}>
                ✓ This will mark the student as Active again and clear the withdrawal date and reason.
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <Btn variant="ghost" onClick={() => setReactivateTarget(null)} disabled={isReactivating} style={{ flex: 1, justifyContent: "center" }}>
                  Cancel
                </Btn>
                <button
                  onClick={handleReactivateConfirm}
                  disabled={isReactivating}
                  style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: isReactivating ? C.slateLight : C.green, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: isReactivating ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}
                >
                  {isReactivating ? <><Spinner /> Reactivating…</> : "Reactivate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline Delete Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────
const DeleteConfirmModal = ({ student, password, onPasswordChange, onCancel, onConfirm, isDeleting }) => {
  if (!student) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, backdropFilter: "blur(2px)", padding: "16px" }}>
      <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 440px)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ background: C.red, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Delete Student Record</div>
          <button onClick={onCancel} disabled={isDeleting} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: isDeleting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "20px" }}>
          {/* Student summary */}
          <div style={{ background: C.bg, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "16px", flexShrink: 0 }}>
              {(student.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "14px", color: C.text }}>{student.name}</div>
              <div style={{ fontSize: "12px", color: C.slate, fontFamily: "monospace", marginTop: "1px" }}>{student.student_id || student.id}</div>
            </div>
          </div>

          {/* Warning */}
          <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: "8px", padding: "10px 14px", fontSize: "13px", color: C.red, fontWeight: 600, marginBottom: "20px" }}>
            🗑️ This will permanently remove the student record. This action <strong>cannot be undone</strong>.
          </div>

          <label style={labelSt}>Enter your password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            disabled={isDeleting}
            placeholder="Account password"
            autoComplete="current-password"
            style={{ ...inputSt, width: "100%", marginBottom: "20px" }}
          />

          {/* Actions */}
          <div style={{ display: "flex", gap: "10px" }}>
            <Btn variant="ghost" onClick={onCancel} disabled={isDeleting} style={{ flex: 1, justifyContent: "center" }}>
              Cancel
            </Btn>
            <button
              onClick={onConfirm}
              disabled={isDeleting || !password}
              style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: isDeleting || !password ? C.slateLight : C.red, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: isDeleting || !password ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}
            >
              {isDeleting ? <><Spinner /> Deleting…</> : "Delete Student"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EMPTY_ARRAY = [];

// ─────────────────────────────────────────────────────────────────────────────
// Base Component
// ─────────────────────────────────────────────────────────────────────────────
function StudentListBase({
  students = EMPTY_ARRAY,
  mode = "manage",
  onDeleteClick,       // External delete handler (used by legacy modes). In manage mode,
                       // the inline DeleteConfirmModal is used instead if onSaveEdit is provided.
  onEditClick,         // External edit handler (navigates away). In manage mode the inline
                       // edit panel is preferred; this prop is used as a fallback.
  onDeleteFiltered,
  onWithdrawStudent,
  onPromoteStudent,
  onPromoteFiltered,
  onSaveEdit,          // (manage mode) async (studentFormData) => bool  — saves the edit in-place
  userRole,
  onLogout,
  showToast,
  canEdit     = true,
  canDelete   = false,
  canWithdraw = true,
  lockedCampusId = null,
  lockedClassId = null,
  lockedClassName = null,
  onBack = null,
}) {
  const navigate = useNavigate();

  // ── Filter / list state ──────────────────────────────────────────────────
  const [searchTerm,         setSearchTerm]         = useState("");
  const [classFilter,        setClassFilter]         = useState("");
  const [sessionFilter,      setSessionFilter]       = useState("");
  const [campusFilter,       setCampusFilter]        = useState("");
  const [statusFilter,       setStatusFilter]        = useState("");
  const [campuses,           setCampuses]            = useState([]);
  const [sessions,           setSessions]            = useState([]);
  const [campusClasses,      setCampusClasses]       = useState([]);
  const [isDeletingFiltered, setIsDeletingFiltered]  = useState(false);
  const [bulkDeleteOpen,     setBulkDeleteOpen]      = useState(false);
  const [bulkDeletePassword, setBulkDeletePassword]  = useState("");
  const [deletePassword,     setDeletePassword]      = useState("");
  const [visibleStudents,    setVisibleStudents]     = useState([]);
  const [isLoadingStudents,  setIsLoadingStudents]   = useState(false);
  const [filterError,        setFilterError]         = useState("");
  const [deleteProgress,     setDeleteProgress]      = useState({ active: false, total: 0 });

  // ── Pagination ─────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(25);

  // ── Withdrawal modal ────────────────────────────────────────────────────
  const [withdrawalModal,  setWithdrawalModal]  = useState(null);
  const [withdrawalForm,   setWithdrawalForm]   = useState({ withdrawal_date: "", reason: "" });
  const [withdrawalErrors, setWithdrawalErrors] = useState({});
  const [isWithdrawing,    setIsWithdrawing]    = useState(false);

  // ── Promote single student modal ────────────────────────────────────────
  const [promoteModal, setPromoteModal] = useState(null);
  const [promoteForm,  setPromoteForm]  = useState({ targetClass: "", targetSection: "" });
  const [isPromoting,  setIsPromoting]  = useState(false);
  const [promoteOptions, setPromoteOptions] = useState([]);
  const [loadingPromoteOptions, setLoadingPromoteOptions] = useState(false);

  // ── Bulk promote modal ───────────────────────────────────────────────────
  const [bulkPromoteOpen, setBulkPromoteOpen] = useState(false);
  const [bulkPromoteForm, setBulkPromoteForm] = useState({ targetClass: "", targetSection: "" });
  const [isBulkPromoting, setIsBulkPromoting] = useState(false);
  const [bulkPromoteOptions, setBulkPromoteOptions] = useState([]);
  const [loadingBulkPromoteOptions, setLoadingBulkPromoteOptions] = useState(false);

  // ── Promotion report ─────────────────────────────────────────────────────
  const [promotionReport, setPromotionReport] = useState([]);
  const [reportOpen,      setReportOpen]      = useState(false);

  // ── Full-page View (manage mode) ─────────────────────────────────────────
  const [viewStudent,  setViewStudent]  = useState(null);

  // ── Withdrawn Students full page ─────────────────────────────────────────
  const [showWithdrawnPage, setShowWithdrawnPage] = useState(false);

  // ── Inline Edit panel (manage mode) ─────────────────────────────────────
  const [editStudent,  setEditStudent]  = useState(null);

  // ── Inline Delete confirm modal (manage mode) ────────────────────────────
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [isDeletingOne,   setIsDeletingOne]   = useState(false);

  // ── Load campuses for superadmin ─────────────────────────────────────────
  useEffect(() => {
    if (userRole !== "superadmin") return;
    const fetchCampuses = async () => {
      const token = localStorage.getItem("als_token");
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) { onLogout?.(); return; }
        if (!res.ok) throw new Error("Failed to fetch campuses");
        const data = await res.json();
        setCampuses(Array.isArray(data) ? data : []);
      } catch (err) { console.error("Failed to fetch campuses", err); }
    };
    fetchCampuses();
  }, [userRole, onLogout]);

  // ── Load academic sessions and campus classes ────────────────────────────
  useEffect(() => {
    let alive = true;
    const scopeCampus = lockedCampusId || campusFilter;
    const scope = scopeCampus ? `?campus_id=${scopeCampus}` : "";

    Promise.all([
      fetch(`${API_BASE_URL}/api/sessions${scope}`, { headers: getAuthHeaders() }),
      fetch(`${API_BASE_URL}/api/classes${scope}`, { headers: getAuthHeaders() }),
    ])
      .then(async ([sessionsRes, classesRes]) => {
        if (!sessionsRes.ok || !classesRes.ok) throw new Error();
        const [sessionsData, classesData] = await Promise.all([sessionsRes.json(), classesRes.json()]);
        if (!alive) return;
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setCampusClasses(Array.isArray(classesData) ? classesData : []);
      })
      .catch(() => {
        if (alive) {
          setSessions([]);
          setCampusClasses([]);
        }
      });
    return () => { alive = false; };
  }, [lockedCampusId, campusFilter]);

  // ── Build session map ─────────────────────────────────────────────────────
  const sessionMap = useMemo(
    () => new Map(sessions.map((session) => [String(session.id), session])),
    [sessions],
  );

  // ── Get available classes filtered by selected session ──────────────────
  const availableClasses = useMemo(
    () => filterClassesBySession(campusClasses, sessionFilter),
    [campusClasses, sessionFilter],
  );

  // ── Get class names for dropdown ────────────────────────────────────────
  const classNames = useMemo(
    () => ["", ...uniqueClassNames(availableClasses)],
    [availableClasses],
  );

  // ── Reset class filter when session changes ─────────────────────────────
  useEffect(() => {
    setClassFilter("");
  }, [sessionFilter]);

  // ── Fetch students ──────────────────────────────────────────────────────
  const hasAnyFilter = Boolean(
    searchTerm.trim() ||
      classFilter ||
      sessionFilter ||
      campusFilter ||
      statusFilter ||
      lockedCampusId ||
      lockedClassId ||
      lockedClassName
  );

  const fetchStudents = useCallback(async () => {
    const token  = localStorage.getItem("als_token");
    const params = new URLSearchParams();

    if (searchTerm.trim()) params.set("search", searchTerm.trim());

    if (lockedClassId) {
      params.set("class_id", lockedClassId);
    } else if (classFilter) {
      // Use the selected class name directly
      params.set("class_name", classFilter);
    }
    
    if (statusFilter) params.set("student_status", statusFilter);
    if (sessionFilter) {
      // Get the session name from the session map
      const session = sessionMap.get(String(sessionFilter));
      if (session) {
        params.set("academic_session", session.name);
      }
    }
    
    const activeCampusId = lockedCampusId || campusFilter;
    if (activeCampusId) {
      params.set("campus_id", activeCampusId);
    } else if (userRole === "superadmin" && campusFilter) {
      params.set("campus_id", campusFilter);
    }

    setIsLoadingStudents(true);
    setFilterError("");

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 401) { onLogout?.(); return; }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Unable to load students");
      }
      const data = await res.json();
      setVisibleStudents(
        Array.isArray(data)
          ? data.map((s) => ({ ...s, id: s.id, student_id: s.student_id }))
          : [],
      );
    } catch (err) {
      console.error(err);
      setVisibleStudents([]);
      setFilterError(err.message || "Unable to load students");
    } finally {
      setIsLoadingStudents(false);
    }
  }, [
    searchTerm,
    classFilter,
    sessionFilter,
    campusFilter,
    statusFilter,
    userRole,
    onLogout,
    lockedCampusId,
    lockedClassId,
    sessionMap,
  ]);

  // Debounce search; apply other filters immediately
  useEffect(() => {
    const delay = searchTerm ? 350 : 0;
    const timer = setTimeout(() => { fetchStudents(); }, delay);
    return () => clearTimeout(timer);
  }, [fetchStudents]);

  // Jump to page 1 whenever the filtered set changes
  useEffect(() => { setCurrentPage(1); }, [fetchStudents]);

  // Withdrawn students are excluded from the main list — they already have
  // their own dedicated "Withdrawn Students" page.
  const displayedStudents = useMemo(
    () => sortStudentsAscending(visibleStudents.filter((s) => s.student_status !== "Withdrawn")),
    [visibleStudents],
  );

  const totalPages = Math.max(1, Math.ceil(displayedStudents.length / pageSize));

  // Clamp the page if promote/delete/withdraw shrinks the list
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedStudents = useMemo(
    () => displayedStudents.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [displayedStudents, currentPage, pageSize],
  );

  // Keep list in sync when parent updates the students prop
  useEffect(() => {
    if (!hasAnyFilter) fetchStudents();
  }, [students]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setSearchTerm("");
    setClassFilter("");
    setSessionFilter("");
    setCampusFilter("");
    setStatusFilter("");
    setFilterError("");
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleDeleteFilteredClick = async () => {
    const canBulkDeleteInMode = mode === "delete" && hasAnyFilter;
    if (!canBulkDeleteInMode || !canDelete || !onDeleteFiltered || displayedStudents.length === 0 || isDeletingFiltered) return;
    setBulkDeletePassword("");
    setBulkDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    const canBulkDeleteInMode = mode === "delete" && hasAnyFilter;
    if (
      !canBulkDeleteInMode ||
      !canDelete ||
      !onDeleteFiltered ||
      displayedStudents.length === 0 ||
      isDeletingFiltered ||
      !bulkDeletePassword
    ) return;

    setIsDeletingFiltered(true);
    setDeleteProgress({ active: true, total: displayedStudents.length });
    try {
      const deletedStudents = await onDeleteFiltered(displayedStudents, bulkDeletePassword);
      const deletedIds = new Set(
        Array.isArray(deletedStudents)
          ? deletedStudents.map((s) => s.id)
          : displayedStudents.map((s) => s.id),
      );
      setVisibleStudents((prev) => prev.filter((s) => !deletedIds.has(s.id)));
      setBulkDeleteOpen(false);
      setBulkDeletePassword("");
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeletingFiltered(false);
      setDeleteProgress({ active: false, total: 0 });
    }
  };

  // ── Inline Delete (manage mode) ──────────────────────────────────────────
  /**
   * Opens the inline delete confirmation panel.
   * In manage mode we handle deletion ourselves.
   * In other modes we delegate to the parent's onDeleteClick prop.
   */
  const handleDeleteButtonClick = (student) => {
    if (mode === "manage") {
      setDeletePassword("");
      setDeleteTarget(student);
    } else {
      onDeleteClick?.(student);
    }
  };

  const handleInlineDeleteConfirm = async () => {
    if (!deleteTarget || !deletePassword) return;
    const token = localStorage.getItem("als_token");
    const apiStudentId = deleteTarget?.student_id || deleteTarget?.id;
    setIsDeletingOne(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/students/${apiStudentId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: deletePassword }),
      });
      if (res.status === 401) {
        const errData = await res.json().catch(() => ({}));
        if (errData.detail === "Invalid password") {
          throw new Error("Invalid password");
        }
        onLogout?.();
        return;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to delete student");
      }
      setVisibleStudents((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeletePassword("");
      showToast?.("success", "Student Removed", `${deleteTarget.name} has been deleted.`);
    } catch (err) {
      console.error(err);
      showToast?.(
        "error",
        err.message === "Invalid password" ? "Authentication Failed" : "Deletion Failed",
        err.message === "Invalid password" ? "The password you entered is incorrect." : "Could not delete the student record.",
      );
    } finally {
      setIsDeletingOne(false);
    }
  };

  // ── Inline Edit (manage mode) ────────────────────────────────────────────
  /**
   * In manage mode, clicking Edit opens the slide-in panel with <StudentForm>.
   * In other modes we delegate to the parent's onEditClick prop.
   */
  const handleEditButtonClick = (student) => {
    if (mode === "manage") {
      setEditStudent(student);
    } else {
      onEditClick?.(student);
    }
  };

  const handleInlineEditSave = async (formData) => {
    if (!onSaveEdit) return false;
    const ok = await onSaveEdit(formData);
    if (ok) {
      setEditStudent(null);
      fetchStudents(); // Refresh list to show updated data
    }
    return ok;
  };

  // ── Withdrawal modal ──────────────────────────────────────────────────────
  const openWithdrawalModal = (student) => {
    setWithdrawalModal(student);
    setWithdrawalForm({ withdrawal_date: student.withdrawal_date || "", reason: student.withdrawal_reason || "" });
    setWithdrawalErrors({});
  };

  const closeWithdrawalModal = () => {
    setWithdrawalModal(null);
    setWithdrawalForm({ withdrawal_date: "", reason: "" });
    setWithdrawalErrors({});
  };

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawalModal || !onWithdrawStudent) return;
    const errs = {};
    if (!withdrawalForm.withdrawal_date) errs.withdrawal_date = "Withdrawal date is required";
    if (!withdrawalForm.reason.trim())   errs.reason           = "Withdrawal reason is required";
    setWithdrawalErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (
      !(await confirmDialog(
        `Withdraw ${withdrawalModal.name}? Fee history and admission records will remain.`,
        { title: "Withdraw Student", confirmLabel: "Withdraw Student", variant: "warning" },
      ))
    ) return;
    setIsWithdrawing(true);
    try {
      const updated = await onWithdrawStudent(withdrawalModal, {
        withdrawal_date: withdrawalForm.withdrawal_date,
        reason: withdrawalForm.reason.trim(),
      });
      if (updated) {
        setVisibleStudents((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        closeWithdrawalModal();
      }
    } finally { setIsWithdrawing(false); }
  };

  // ── Promote single student ────────────────────────────────────────────────
  const resolvePromoteCampusId = useCallback((student) => {
    if (student?.campus_id) return student.campus_id;
    if (userRole === "superadmin" && campusFilter) return campusFilter;
    return null;
  }, [userRole, campusFilter]);

  const fetchCampusPromoteOptions = useCallback(async (campusId, currentClassName) => {
    const token = localStorage.getItem("als_token");
    const query = campusId ? `?campus_id=${campusId}` : "";
    const res = await fetch(`${API_BASE_URL}/api/classes${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      onLogout?.();
      return [];
    }
    if (!res.ok) throw new Error("Failed to load campus classes");
    const data = await res.json();
    const classes = Array.isArray(data) ? data : [];
    const storedSections = getStoredCampusSections(campusId);
    const expanded = expandClassSectionOptions(classes, storedSections);
    return getNextPromoteOptions(expanded, currentClassName);
  }, [onLogout]);

  const openPromoteModal = async (student) => {
    setPromoteModal(student);
    setPromoteForm({ targetClass: "", targetSection: "" });
    setPromoteOptions([]);
    setLoadingPromoteOptions(true);
    try {
      const options = await fetchCampusPromoteOptions(
        resolvePromoteCampusId(student),
        student.class_name,
      );
      setPromoteOptions(options);
      if (options.length) {
        setPromoteForm({
          targetClass: options[0].className,
          targetSection: options[0].section || "",
        });
      } else {
        setPromoteForm({ targetClass: "Graduate", targetSection: "" });
      }
    } catch (err) {
      console.error("Failed to load promote options", err);
      setPromoteOptions([]);
      setPromoteForm({ targetClass: "Graduate", targetSection: "" });
      showToast?.("error", "Load Error", err.message || "Could not load campus classes.");
    } finally {
      setLoadingPromoteOptions(false);
    }
  };

  const closePromoteModal = () => {
    setPromoteModal(null);
    setPromoteForm({ targetClass: "", targetSection: "" });
    setPromoteOptions([]);
  };

  const recordPromotion = (before, after, isGraduating) => {
    setPromotionReport((prev) => [...prev, {
      student_id:   before.student_id || before.id,
      name:         before.name,
      from_class:   before.class_name || "—",
      from_section: before.class_section || "—",
      to_class:     isGraduating ? "Graduated" : (after.class_name || "—"),
      to_section:   isGraduating ? "—" : (after.class_section || "—"),
      date:         new Date().toISOString().slice(0, 10),
    }]);
  };

  const handlePromoteSubmit = async (e) => {
    e.preventDefault();
    if (!promoteModal || !onPromoteStudent || !promoteForm.targetClass) return;
    const isGraduating = promoteForm.targetClass === "Graduate";
    const targetLabel = isGraduating
      ? "Graduate"
      : formatPromoteLabel(promoteForm.targetClass, promoteForm.targetSection);
    const confirmMsg = isGraduating
      ? `Mark ${promoteModal.name} as Graduated?`
      : `Promote ${promoteModal.name} from ${promoteModal.class_name || "—"} to ${targetLabel}?`;
    if (
      !(await confirmDialog(
        confirmMsg,
        { title: isGraduating ? "Graduate Student" : "Promote Student", confirmLabel: isGraduating ? "Mark Graduated" : "Promote", variant: "warning" },
      ))
    ) return;
    setIsPromoting(true);
    try {
      const payload = isGraduating
        ? { student_status: "Graduated" }
        : { class_name: promoteForm.targetClass, class_section: promoteForm.targetSection || promoteModal.class_section || "" };
      const updated = await onPromoteStudent(promoteModal, payload);
      if (updated) {
        setVisibleStudents((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        recordPromotion(promoteModal, updated, isGraduating);
        closePromoteModal();
        setReportOpen(true);
      }
    } finally { setIsPromoting(false); }
  };

  // ── Bulk promote ───────────────────────────────────────────────────────────
  const openBulkPromoteModal = async () => {
    setBulkPromoteForm({ targetClass: "", targetSection: "" });
    setBulkPromoteOptions([]);
    setBulkPromoteOpen(true);
    setLoadingBulkPromoteOptions(true);
    try {
      const sample = displayedStudents[0];
      const options = await fetchCampusPromoteOptions(
        resolvePromoteCampusId(sample),
        classFilter,
      );
      setBulkPromoteOptions(options);
      if (options.length) {
        setBulkPromoteForm({
          targetClass: options[0].className,
          targetSection: options[0].section || "",
        });
      } else {
        setBulkPromoteForm({ targetClass: "Graduate", targetSection: "" });
      }
    } catch (err) {
      console.error("Failed to load bulk promote options", err);
      setBulkPromoteOptions([]);
      setBulkPromoteForm({ targetClass: "Graduate", targetSection: "" });
      showToast?.("error", "Load Error", err.message || "Could not load campus classes.");
    } finally {
      setLoadingBulkPromoteOptions(false);
    }
  };

  const closeBulkPromoteModal = () => {
    setBulkPromoteOpen(false);
    setBulkPromoteForm({ targetClass: "", targetSection: "" });
    setBulkPromoteOptions([]);
  };

  const handleBulkPromoteSubmit = async (e) => {
    e.preventDefault();
    if (!onPromoteFiltered || !bulkPromoteForm.targetClass || displayedStudents.length === 0) return;
    const isGraduating = bulkPromoteForm.targetClass === "Graduate";
    const targetLabel = isGraduating
      ? "Graduate"
      : formatPromoteLabel(bulkPromoteForm.targetClass, bulkPromoteForm.targetSection);
    const confirmMsg = isGraduating
      ? `Mark all ${displayedStudents.length} student(s) in Class ${classFilter} as Graduated?`
      : `Promote all ${displayedStudents.length} student(s) in Class ${classFilter} to ${targetLabel}?`;
    if (
      !(await confirmDialog(
        confirmMsg,
        { title: isGraduating ? "Graduate Class" : "Promote Class", confirmLabel: isGraduating ? "Mark Graduated" : "Promote Class", variant: "warning" },
      ))
    ) return;
    setIsBulkPromoting(true);
    try {
      const payload = isGraduating
        ? { student_status: "Graduated" }
        : {
            class_name: bulkPromoteForm.targetClass,
            ...(bulkPromoteForm.targetSection
              ? { class_section: bulkPromoteForm.targetSection }
              : {}),
          };
      const targetStudents = displayedStudents.slice();
      const results = await onPromoteFiltered(targetStudents, payload);
      if (results && results.length > 0) {
        results.forEach(({ before, after }) => recordPromotion(before, after, isGraduating));
        setReportOpen(true);
        fetchStudents();
      }
      closeBulkPromoteModal();
    } finally { setIsBulkPromoting(false); }
  };

  // ── Promotion report: print + CSV export ─────────────────────────────────
  const handleExportPromotionCSV = () => {
    const rows = [
      ["#", "Name", "Student ID", "From Class", "From Section", "To Class", "To Section", "Date"],
      ...promotionReport.map((p, i) => [i + 1, p.name, p.student_id, p.from_class, p.from_section, p.to_class, p.to_section, p.date]),
    ];
    const csv  = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url  = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url; link.download = `Promotion_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  };

  const handlePrintPromotionReport = () => {
    const now      = new Date();
    const dateStr  = now.toLocaleDateString("en-PK", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr  = now.toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
    const logoUrl  = `${window.location.origin}${process.env.PUBLIC_URL}/als-logo.png`;
    const graduated = promotionReport.filter((p) => p.to_class === "Graduated").length;

    const rows = promotionReport.map((p, i) => `
      <tr style="background:${i % 2 === 0 ? "#fff" : "#f8f9fc"}">
        <td>${i + 1}</td>
        <td><strong>${p.name || "—"}</strong></td>
        <td style="font-family:monospace">${p.student_id || "—"}</td>
        <td>${p.from_class}${p.from_section && p.from_section !== "—" ? " - " + p.from_section : ""}</td>
        <td>${p.to_class}${p.to_section && p.to_section !== "—" ? " - " + p.to_section : ""}</td>
        <td>${p.date}</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Promotion Report — American Lycetuff DNK School System</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #1a1d23; background: #fff; }
    @page { margin: 18mm 14mm; }
    .header { background: #0f1b2d; color: #fff; padding: 20px 24px; border-radius: 8px 8px 0 0; display: flex; justify-content: space-between; align-items: center; }
    .header-left { display: flex; align-items: center; gap: 14px; }
    .school-logo { width: 46px; height: 46px; border-radius: 8px; object-fit: contain; background: #fff; padding: 3px; }
    .school-name { font-size: 17px; font-weight: 800; letter-spacing: 0.3px; }
    .report-label { font-size: 11px; color: rgba(255,255,255,0.7); margin-top: 3px; text-transform: uppercase; letter-spacing: 0.8px; }
    .header-right { text-align: right; font-size: 11px; color: rgba(255,255,255,0.75); }
    .summary { display: flex; border: 1px solid #e2e8f0; border-top: none; }
    .stat-box { flex: 1; padding: 12px 16px; border-right: 1px solid #e2e8f0; }
    .stat-box:last-child { border-right: none; }
    .stat-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
    .stat-value { font-size: 22px; font-weight: 800; }
    .main-table { width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; margin-top: 16px; }
    .main-table th { padding: 9px 10px; font-size: 10px; font-weight: 700; color: #64748b; background: #f4f6f9; border-bottom: 2px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.4px; white-space: nowrap; }
    .main-table td { padding: 8px 10px; font-size: 11.5px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
    .footer { margin-top: 20px; padding-top: 12px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <img class="school-logo" src="${logoUrl}" alt="School Logo" />
      <div>
        <div class="school-name">American Lycetuff DNK School System</div>
        <div class="report-label">Student Promotion Report</div>
      </div>
    </div>
    <div class="header-right"><div>${dateStr}</div><div>${timeStr}</div></div>
  </div>
  <div class="summary">
    <div class="stat-box"><div class="stat-label">Total Promoted</div><div class="stat-value" style="color:#0f1b2d">${promotionReport.length}</div></div>
    <div class="stat-box"><div class="stat-label">Graduated</div><div class="stat-value" style="color:#1d4ed8">${graduated}</div></div>
    <div class="stat-box"><div class="stat-label">Moved to New Class</div><div class="stat-value" style="color:#16a34a">${promotionReport.length - graduated}</div></div>
  </div>
  <table class="main-table">
    <thead><tr><th>#</th><th>Name</th><th>Student ID</th><th>From</th><th>To</th><th>Date</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="6" style="text-align:center;padding:30px;color:#94a3b8">No promotions recorded</td></tr>'}</tbody>
  </table>
  <div class="footer">
    <div>Generated by ALS Portal · ${dateStr} ${timeStr}</div>
    <div>Total: ${promotionReport.length} student(s)</div>
  </div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=1000,height=750");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  const modeConfig = MODE[mode] || MODE.manage;

  // ── Full-page routes rendered instead of the list ────────────────────────
  if (viewStudent) {
    return (
      <FullPageStudentView
        student={viewStudent}
        onBack={() => setViewStudent(null)}
        onEditClick={(student) => { setViewStudent(null); handleEditButtonClick(student); }}
        canEdit={canEdit}
      />
    );
  }

  if (showWithdrawnPage) {
    return (
      <WithdrawnStudentsPage
        onBack={() => setShowWithdrawnPage(false)}
        onLogout={onLogout}
        showToast={showToast}
      />
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
        <button onClick={() => onBack ? onBack() : navigate("/dashboard")} style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 38, height: 38, borderRadius: "9px",
          border: `1.5px solid ${C.border}`, background: C.white,
          cursor: "pointer", color: C.slate, flexShrink: 0,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
          <ArrowLeftIcon />
        </button>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 800, color: C.text, margin: 0 }}>
            {lockedClassName ? `${lockedClassName} — Students` : modeConfig.title}
          </h1>
          <p style={{ fontSize: "13px", color: C.slate, margin: "3px 0 0" }}>
            {lockedClassName ? `Manage student profiles and promotions for ${lockedClassName}` : modeConfig.subtitle}
          </p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          {canWithdraw && (
            <Btn variant="danger" onClick={() => setShowWithdrawnPage(true)}>
              <AlertIcon /> 
            </Btn>
          )}
          {promotionReport.length > 0 && (
            <Btn variant="green" onClick={() => setReportOpen(true)}>
              📋 Promotion Report ({promotionReport.length})
            </Btn>
          )}
          {mode !== "manage" && (
            <div style={{ padding: "5px 14px", borderRadius: "20px", background: `${modeConfig.accentColor}18`, border: `1px solid ${modeConfig.accentColor}33`, fontSize: "12px", fontWeight: 700, color: modeConfig.accentColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {mode} mode
            </div>
          )}
        </div>
      </div>

      {/* ── Filter + table card ───────────────────────────────────────────── */}
      <div style={{ background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>

        {/* Card header */}
        <div style={{ background: C.navy, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span style={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>Student Records</span>
            <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", marginLeft: "10px" }}>
              {hasAnyFilter ? "Showing filtered results" : "Showing all students"}
            </span>
          </div>
          {!isLoadingStudents && (
            <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", background: "rgba(255,255,255,0.12)", padding: "3px 12px", borderRadius: "20px", fontWeight: 600 }}>
              {displayedStudents.length} student{displayedStudents.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Filter controls */}
        <div style={{ padding: "14px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${C.border}`, background: C.bg }}>

          {/* Search */}
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", flex: "1 1 220px" }}>
            <label style={labelSt}>Search</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: C.slateLight, display: "flex", pointerEvents: "none" }}>
                <SearchIcon />
              </span>
              <input
                type="text"
                placeholder="Name or student ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...inputSt, width: "100%", paddingLeft: "36px" }}
              />
            </div>
          </div>

          {/* Session — first in the filter order */}
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: "150px" }}>
            <label style={labelSt}>Session</label>
            <select 
              value={sessionFilter} 
              onChange={(e) => setSessionFilter(e.target.value)} 
              style={inputSt}
            >
              <option value="">All Sessions</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Class — filtered by selected session */}
          {!lockedClassId && !lockedClassName && (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: "130px" }}>
              <label style={labelSt}>Class</label>
              <select 
                value={classFilter} 
                onChange={(e) => setClassFilter(e.target.value)} 
                style={inputSt}
              >
                <option value="">All Classes</option>
                {classNames.filter(c => c !== "").map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status */}
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: "130px" }}>
            <label style={labelSt}>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputSt}>
              <option value="">All Statuses</option>
              {["Active", "Inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Campus (superadmin only) */}
          {userRole === "superadmin" && !lockedCampusId && (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px", minWidth: "150px" }}>
              <label style={labelSt}>Campus</label>
              <select value={campusFilter} onChange={(e) => {
                setCampusFilter(e.target.value);
                // Reset session and class when campus changes
                setSessionFilter("");
                setClassFilter("");
              }} style={inputSt}>
                <option value="">All Campuses</option>
                {campuses.map((c) => <option key={c.id} value={c.id.toString()}>{c.name}</option>)}
              </select>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
            {hasAnyFilter && (
              <Btn variant="ghost" onClick={clearFilters}>Clear Filters</Btn>
            )}
            {(mode === "delete") && canDelete && (
              <Btn
                variant="danger"
                onClick={handleDeleteFilteredClick}
                disabled={
                  isDeletingFiltered ||
                  displayedStudents.length === 0 ||
                  !hasAnyFilter
                }
              >
                {isDeletingFiltered
                  ? "Deleting..."
                  : !hasAnyFilter
                    ? "Bulk Delete (Apply Filter)"
                    : `Bulk Delete (${displayedStudents.length})`}
              </Btn>
            )}
            {onPromoteFiltered && canEdit && classFilter && displayedStudents.length > 0 && (
              <Btn variant="green" onClick={openBulkPromoteModal}>
                <PromoteIcon /> Promote Class ({displayedStudents.length})
              </Btn>
            )}
          </div>
        </div>

        {/* ── Table / empty / error / loading states ───────────────────── */}
        {isLoadingStudents ? (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ width: 40, height: 40, border: `3px solid ${C.navy}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 16px" }} />
            <div style={{ fontWeight: 700, fontSize: "14px", color: C.text, marginBottom: "4px" }}>
              {hasAnyFilter ? "Loading filtered records…" : "Loading all students…"}
            </div>
            <div style={{ fontSize: "13px" }}>Please wait.</div>
          </div>

        ) : filterError ? (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ fontSize: "36px", marginBottom: "12px" }}>⚠️</div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.red, marginBottom: "6px" }}>Could Not Load Students</div>
            <div style={{ fontSize: "13px", color: C.slate, marginBottom: "16px" }}>{filterError}</div>
            <Btn onClick={fetchStudents}>Retry</Btn>
          </div>

        ) : displayedStudents.length === 0 ? (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>📭</div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>
              {hasAnyFilter ? "No Students Match Your Filters" : "No Students Found"}
            </div>
            <div style={{ fontSize: "13px" }}>
              {hasAnyFilter ? "Try adjusting your search or filter criteria." : "No student records exist in the database yet."}
            </div>
            {hasAnyFilter && (
              <div style={{ marginTop: "14px" }}>
                <Btn variant="ghost" onClick={clearFilters}>Clear Filters</Btn>
              </div>
            )}
          </div>

        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <TH>#</TH>
                  <TH>Student ID</TH>
                  <TH>Name</TH>
                  <TH>Gender</TH>
                  <TH>Class</TH>
                  <TH>Status</TH>
                  <TH>Admitted</TH>
                  <TH center>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {pagedStudents.map((student, idx) => {
                  const rowBg = idx % 2 === 0 ? C.white : "#fafbfd";
                  return (
                    <tr key={student.id} style={{ background: rowBg }}
                      onMouseEnter={(e) => e.currentTarget.style.background = C.blueSoft}
                      onMouseLeave={(e) => e.currentTarget.style.background = rowBg}>
                      <TD>
                        <span style={{ color: C.slateLight, fontWeight: 600, fontSize: "12px" }}>{(currentPage - 1) * pageSize + idx + 1}</span>
                      </TD>
                      <TD mono>
                        <strong>{student.student_id || student.id}</strong>
                      </TD>
                      <TD>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "13px", flexShrink: 0 }}>
                            {(student.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 700 }}>{student.name}</span>
                        </div>
                      </TD>
                      <TD>{student.gender || "—"}</TD>
                      <TD>
                        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: C.blueSoft, color: C.blue }}>
                          {student.class_name || "—"}
                        </span>
                      </TD>
                      <TD><StatusBadge status={student.student_status} /></TD>
                      <TD>
                        <span style={{ fontSize: "12px", color: C.slate }}>{student.admission_date || "—"}</span>
                      </TD>

                      {/* ── Action buttons ── */}
                      <TD center>
                        <div style={{ display: "flex", gap: "5px", justifyContent: "center", flexWrap: "nowrap" }}>

                          {/* View — always visible in manage mode */}
                          {mode === "manage" && (
                            <button
                              id={`btn-view-${student.id}`}
                              onClick={() => setViewStudent(student)}
                              title="View Student Details"
                              style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.blue, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <ViewIcon />
                            </button>
                          )}

                          {/* Edit */}
                          {canEdit && (
                            <button
                              id={`btn-edit-${student.id}`}
                              onClick={() => handleEditButtonClick(student)}
                              title="Edit Record"
                              style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.navy, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <EditIcon />
                            </button>
                          )}

                          {/* Promote — only for Active students */}
                          {onPromoteStudent && canEdit && mode !== "delete" && student.student_status === "Active" && (
                            <button
                              id={`btn-promote-${student.id}`}
                              onClick={() => openPromoteModal(student)}
                              title="Promote Student"
                              style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.green, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <PromoteIcon />
                            </button>
                          )}

                          {/* Withdraw */}
                          {(mode === "view" || mode === "manage") && canWithdraw && (
                            <button
                              id={`btn-withdraw-${student.id}`}
                              onClick={() => openWithdrawalModal(student)}
                              title={student.student_status === "Withdrawn" ? "Update Withdrawal" : "Withdraw Student"}
                              style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.amber, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <AlertIcon />
                            </button>
                          )}

                          {/* Delete */}
                          {(mode === "delete" || mode === "view" || mode === "manage") && canDelete && (
                            <button
                              id={`btn-delete-${student.id}`}
                              onClick={() => handleDeleteButtonClick(student)}
                              title="Delete Record"
                              style={{ width: 32, height: 32, borderRadius: "7px", border: "none", background: C.red, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <DeleteIcon />
                            </button>
                          )}
                        </div>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ───────────────────────────────────────────────── */}
        {!isLoadingStudents && !filterError && displayedStudents.length > 0 && (
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", background: C.bg }}>
            <div style={{ fontSize: "12px", color: C.slate }}>
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong>–<strong>{Math.min(currentPage * pageSize, displayedStudents.length)}</strong> of <strong>{displayedStudents.length}</strong>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} style={{ ...inputSt, padding: "6px 10px", width: "auto" }}>
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <Btn variant="ghost" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "6px 14px" }}>
                ‹ Prev
              </Btn>
              <span style={{ fontSize: "12px", color: C.slate, fontWeight: 600, minWidth: "90px", textAlign: "center" }}>
                Page {currentPage} of {totalPages}
              </span>
              <Btn variant="ghost" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "6px 14px" }}>
                Next ›
              </Btn>
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* MODALS & PANELS                                                     */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      {/* ── Inline Edit Panel (manage mode) ──────────────────────────────── */}
      <SlidePanel
        open={Boolean(editStudent)}
        onClose={() => setEditStudent(null)}
        title={`Edit — ${editStudent?.name || ""}`}
        subtitle="Update student information"
        accentColor={C.navy}
        wide
      >
        {editStudent && (
          <StudentForm
            key={editStudent.id}
            initialData={editStudent}
            onSave={handleInlineEditSave}
            onCancel={() => setEditStudent(null)}
            userRole={userRole}
            showToast={showToast}
            onLogout={onLogout}
          />
        )}
      </SlidePanel>

      {/* ── Inline Delete Confirmation Modal (manage mode) ────────────────── */}
      {mode === "manage" && (
        <DeleteConfirmModal
          student={deleteTarget}
          password={deletePassword}
          onPasswordChange={setDeletePassword}
          onCancel={() => {
            setDeleteTarget(null);
            setDeletePassword("");
          }}
          onConfirm={handleInlineDeleteConfirm}
          isDeleting={isDeletingOne}
        />
      )}

      {/* ── Bulk delete confirmation modal ─────────────────────────────────── */}
      {mode === "delete" && bulkDeleteOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)", padding: "16px" }}>
          <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 480px)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
            <div style={{ background: C.red, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Bulk Delete Students</div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.72)", marginTop: "2px" }}>
                  This will permanently delete filtered records
                </div>
              </div>
              <button
                onClick={() => {
                  if (!isDeletingFiltered) {
                    setBulkDeleteOpen(false);
                    setBulkDeletePassword("");
                  }
                }}
                disabled={isDeletingFiltered}
                style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: isDeletingFiltered ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}
              >
                x
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: "10px", padding: "14px 16px", marginBottom: "18px" }}>
                <div style={{ fontWeight: 800, color: C.red, fontSize: "14px", marginBottom: "6px" }}>
                  Are you sure you want to delete {displayedStudents.length} student record{displayedStudents.length === 1 ? "" : "s"}?
                </div>
                <div style={{ color: C.slate, fontSize: "12.5px", lineHeight: 1.5 }}>
                  Only the currently filtered students will be deleted. This action cannot be undone.
                </div>
              </div>

              <label style={labelSt}>Enter your password</label>
              <input
                type="password"
                value={bulkDeletePassword}
                onChange={(e) => setBulkDeletePassword(e.target.value)}
                disabled={isDeletingFiltered}
                placeholder="Account password"
                autoComplete="current-password"
                style={{ ...inputSt, width: "100%" }}
              />

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <Btn
                  variant="ghost"
                  onClick={() => {
                    setBulkDeleteOpen(false);
                    setBulkDeletePassword("");
                  }}
                  disabled={isDeletingFiltered}
                  style={{ flex: 1, justifyContent: "center" }}
                >
                  Cancel
                </Btn>
                <button
                  type="button"
                  onClick={handleConfirmBulkDelete}
                  disabled={isDeletingFiltered || !bulkDeletePassword}
                  style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: isDeletingFiltered || !bulkDeletePassword ? C.slateLight : C.red, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: isDeletingFiltered || !bulkDeletePassword ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}
                >
                  {isDeletingFiltered ? <><Spinner /> Deleting...</> : "Delete Students"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Withdrawal modal ───────────────────────────────────────────────── */}
      {withdrawalModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)", padding: "16px" }}>
          <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 480px)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>

            <div style={{ background: C.navy, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Withdraw Student</div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", marginTop: "2px" }}>This action can be updated later</div>
              </div>
              <button onClick={closeWithdrawalModal} disabled={isWithdrawing} style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>
                ✕
              </button>
            </div>

            <form onSubmit={handleWithdrawalSubmit}>
              <div style={{ padding: "20px" }}>

                {/* Student summary */}
                <div style={{ background: C.bg, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "16px", flexShrink: 0 }}>
                    {(withdrawalModal.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "14px", color: C.text }}>{withdrawalModal.name}</div>
                    <div style={{ fontSize: "12px", color: C.slate, marginTop: "1px" }}>{withdrawalModal.student_id || withdrawalModal.id}</div>
                  </div>
                </div>

                {/* Withdrawal date */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={labelSt}>Withdrawal Date <span style={{ color: C.red }}>*</span></label>
                  <input type="date"
                    value={withdrawalForm.withdrawal_date}
                    onChange={(e) => setWithdrawalForm((p) => ({ ...p, withdrawal_date: e.target.value }))}
                    style={{ ...inputSt, width: "100%", border: `1.5px solid ${withdrawalErrors.withdrawal_date ? C.red : C.border}`, background: withdrawalErrors.withdrawal_date ? C.redSoft : C.white }}
                  />
                  {withdrawalErrors.withdrawal_date && <span style={errorSt}>{withdrawalErrors.withdrawal_date}</span>}
                </div>

                {/* Reason */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={labelSt}>Withdrawal Reason <span style={{ color: C.red }}>*</span></label>
                  <textarea rows={4}
                    placeholder="Type the reason for this withdrawal…"
                    value={withdrawalForm.reason}
                    onChange={(e) => setWithdrawalForm((p) => ({ ...p, reason: e.target.value }))}
                    style={{ ...inputSt, width: "100%", resize: "vertical", border: `1.5px solid ${withdrawalErrors.reason ? C.red : C.border}`, background: withdrawalErrors.reason ? C.redSoft : C.white }}
                  />
                  {withdrawalErrors.reason && <span style={errorSt}>{withdrawalErrors.reason}</span>}
                </div>

                {/* Warning note */}
                <div style={{ background: C.amberSoft, border: "1px solid #f6e05e", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: C.amber, fontWeight: 600, marginBottom: "20px" }}>
                  ⚠️ The student will be marked Withdrawn. Fee records and admission history will stay in the database.
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: "10px" }}>
                  <Btn variant="ghost" onClick={closeWithdrawalModal} disabled={isWithdrawing} style={{ flex: 1, justifyContent: "center" }}>
                    Cancel
                  </Btn>
                  <button type="submit" disabled={isWithdrawing} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: isWithdrawing ? C.slateLight : C.red, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: isWithdrawing ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}>
                    {isWithdrawing ? <><Spinner /> Withdrawing…</> : "Withdraw Student"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Promote single student modal ───────────────────────────────────── */}
      {promoteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)", padding: "16px" }}>
          <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 440px)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
            <div style={{ background: C.green, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Promote Student</div>
              <button onClick={closePromoteModal} disabled={isPromoting} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: "pointer", fontSize: "16px" }}>✕</button>
            </div>
            <form onSubmit={handlePromoteSubmit}>
              <div style={{ padding: "20px" }}>
                <div style={{ background: C.bg, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "16px", flexShrink: 0 }}>
                    {(promoteModal.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "14px", color: C.text }}>{promoteModal.name}</div>
                    <div style={{ fontSize: "12px", color: C.slate, marginTop: "1px" }}>
                      Currently: {promoteModal.class_name || "—"}{promoteModal.class_section ? ` - ${promoteModal.class_section}` : ""}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label style={labelSt}>Promote To</label>
                  <select
                    value={
                      loadingPromoteOptions || !promoteForm.targetClass
                        ? ""
                        : promoteForm.targetClass === "Graduate"
                          ? "Graduate"
                          : encodePromoteTarget(promoteForm.targetClass, promoteForm.targetSection)
                    }
                    onChange={(e) => {
                      const next = decodePromoteTarget(e.target.value);
                      setPromoteForm({
                        targetClass: next.className,
                        targetSection: next.section,
                      });
                    }}
                    disabled={loadingPromoteOptions}
                    style={{ ...inputSt, width: "100%" }}
                  >
                    {loadingPromoteOptions && (
                      <option value="">Loading campus classes…</option>
                    )}
                    {!loadingPromoteOptions && promoteOptions.length === 0 && (
                      <option value="Graduate">Graduate</option>
                    )}
                    {!loadingPromoteOptions && promoteOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {!loadingPromoteOptions && promoteOptions.length > 0 && (
                      <option value="Graduate">Graduate</option>
                    )}
                  </select>
                  {!loadingPromoteOptions && promoteOptions.length === 0 && (
                    <span style={{ display: "block", fontSize: "11px", color: C.slate, marginTop: 6 }}>
                      No next campus class found. You can mark the student as Graduated.
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <Btn variant="ghost" onClick={closePromoteModal} disabled={isPromoting} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
                  <button type="submit" disabled={isPromoting || loadingPromoteOptions || !promoteForm.targetClass} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: isPromoting || loadingPromoteOptions ? C.slateLight : C.green, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: isPromoting || loadingPromoteOptions ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}>
                    {isPromoting ? <><Spinner /> Promoting…</> : "Promote"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Promote whole class modal ───────────────────────────────────────── */}
      {bulkPromoteOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)", padding: "16px" }}>
          <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 440px)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
            <div style={{ background: C.green, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Promote Whole Class</div>
              <button onClick={closeBulkPromoteModal} disabled={isBulkPromoting} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: "pointer", fontSize: "16px" }}>✕</button>
            </div>
            <form onSubmit={handleBulkPromoteSubmit}>
              <div style={{ padding: "20px" }}>
                <div style={{ background: C.bg, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", fontSize: "13px", color: C.text }}>
                  Promoting <strong>{displayedStudents.length}</strong> student{displayedStudents.length === 1 ? "" : "s"} currently in <strong>Class {classFilter}</strong>.
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label style={labelSt}>Promote To</label>
                  <select
                    value={
                      loadingBulkPromoteOptions || !bulkPromoteForm.targetClass
                        ? ""
                        : bulkPromoteForm.targetClass === "Graduate"
                          ? "Graduate"
                          : encodePromoteTarget(bulkPromoteForm.targetClass, bulkPromoteForm.targetSection)
                    }
                    onChange={(e) => {
                      const next = decodePromoteTarget(e.target.value);
                      setBulkPromoteForm({
                        targetClass: next.className,
                        targetSection: next.section,
                      });
                    }}
                    disabled={loadingBulkPromoteOptions}
                    style={{ ...inputSt, width: "100%" }}
                  >
                    {loadingBulkPromoteOptions && (
                      <option value="">Loading campus classes…</option>
                    )}
                    {!loadingBulkPromoteOptions && bulkPromoteOptions.length === 0 && (
                      <option value="Graduate">Graduate</option>
                    )}
                    {!loadingBulkPromoteOptions && bulkPromoteOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {!loadingBulkPromoteOptions && bulkPromoteOptions.length > 0 && (
                      <option value="Graduate">Graduate</option>
                    )}
                  </select>
                  {!loadingBulkPromoteOptions && bulkPromoteOptions.length === 0 && (
                    <span style={{ display: "block", fontSize: "11px", color: C.slate, marginTop: 6 }}>
                      No next campus class found. You can mark the class as Graduated.
                    </span>
                  )}
                </div>

                <div style={{ background: C.amberSoft, border: "1px solid #f6e05e", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: C.amber, fontWeight: 600, marginBottom: "20px" }}>
                  ⚠️ This updates every student currently shown in this filtered list. This cannot be undone in bulk.
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <Btn variant="ghost" onClick={closeBulkPromoteModal} disabled={isBulkPromoting} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
                  <button type="submit" disabled={isBulkPromoting || loadingBulkPromoteOptions || !bulkPromoteForm.targetClass} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: isBulkPromoting || loadingBulkPromoteOptions ? C.slateLight : C.green, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: isBulkPromoting || loadingBulkPromoteOptions ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}>
                    {isBulkPromoting
                      ? <><Spinner /> Promoting…</>
                      : `Promote ${displayedStudents.length} Student${displayedStudents.length === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Promotion report modal ────────────────────────────────────────────── */}
      {reportOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)", padding: "16px" }}>
          <div style={{ background: C.white, borderRadius: "14px", width: "min(95vw, 720px)", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ background: C.navy, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>Promotion Report</div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", marginTop: "2px" }}>{promotionReport.length} student{promotionReport.length === 1 ? "" : "s"} promoted this session</div>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Btn variant="ghost" onClick={handleExportPromotionCSV} disabled={promotionReport.length === 0}>⬇ CSV</Btn>
                <Btn onClick={handlePrintPromotionReport} disabled={promotionReport.length === 0}>🖨 Print</Btn>
                <button onClick={() => setReportOpen(false)} style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: "pointer", fontSize: "16px" }}>✕</button>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <TH>#</TH><TH>Name</TH><TH>Student ID</TH><TH>From</TH><TH>To</TH><TH>Date</TH>
                  </tr>
                </thead>
                <tbody>
                  {promotionReport.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: "40px", textAlign: "center", color: C.slate }}>No promotions recorded yet.</td></tr>
                  ) : promotionReport.map((p, i) => (
                    <tr key={`${p.student_id}-${i}`} style={{ background: i % 2 === 0 ? C.white : "#fafbfd" }}>
                      <TD>{i + 1}</TD>
                      <TD><strong>{p.name}</strong></TD>
                      <TD mono>{p.student_id}</TD>
                      <TD>{p.from_class}{p.from_section !== "—" ? ` - ${p.from_section}` : ""}</TD>
                      <TD>{p.to_class}{p.to_section !== "—" ? ` - ${p.to_section}` : ""}</TD>
                      <TD>{p.date}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete progress overlay (bulk delete in non-manage modes) ────────── */}
      {deleteProgress.active && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(3px)" }}>
          <div style={{ background: C.white, borderRadius: "16px", padding: "40px 48px", textAlign: "center", boxShadow: "0 24px 60px rgba(0,0,0,0.3)", maxWidth: "340px", width: "90%" }}>
            <div style={{ width: 48, height: 48, border: `3px solid ${C.red}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 20px" }} />
            <div style={{ fontWeight: 800, fontSize: "16px", color: C.text, marginBottom: "6px" }}>Deleting records</div>
            <div style={{ fontSize: "13px", color: C.slate }}>
              Removing {deleteProgress.total} student record{deleteProgress.total === 1 ? "" : "s"} from the database.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentListBase;